import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import analyticsEmptyState from '../../../../assets/images/analyticsEmptyState.svg';
import {AnalyticsMetric} from '../../../../models/api/analyticsMetricsModel';
import {AnalyticsComputedGraphMetric} from '../../../transformers/analyticsMetricsTransformers';

/*
 * Styles.
 */

const EmptyStateWrapper = styled.div<{$width: string; $height: string}>`
  width: ${(p) => p.$width};
  height: ${(p) => p.$height};
  min-height: 100px;

  ${flexCenter}
  justify-content: center;

  color: ${(p) => p.theme.greys.shade70};
  flex-direction: column;
`;

const EmptyStateImage = styled.img`
  width: 70px;
  height: 70px;
`;

const EmptyStateTitle = styled.div`
  margin-top: 16px;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
`;

const EmptyStateSubtitle = styled.div`
  margin-top: 8px;
  font-size: ${fontSizes.verySmall};
`;

export type EmptyStateRenderer = RendererOf<{
  width?: number;
  height?: number;
  image?: string;
  onCollapse?: () => void;
}>;

/*
 * Props.
 */

interface EmptyStateProps {
  width?: number;
  height?: number;
  title: ReactNode;
  subtitle: ReactNode;
  image?: string;
}

/*
 * Constants.
 */

const DefaultEmptyStateImage = analyticsEmptyState;

/*
 * Component.
 */

export const EmptyState: FC<EmptyStateProps> = ({width, height, title, subtitle, image}) => (
  <EmptyStateWrapper $width={width ? `${width}px` : '100%'} $height={height ? `${height}px` : '100%'}>
    <EmptyStateImage src={image ?? DefaultEmptyStateImage} />
    <EmptyStateTitle>{title}</EmptyStateTitle>
    <EmptyStateSubtitle>{subtitle}</EmptyStateSubtitle>
  </EmptyStateWrapper>
);

/*
 * Helpers.
 */

export const defaultEmptyStateRenderer: EmptyStateRenderer = ({width, height, image}) => (
  <EmptyState
    title={<Trans id="analytics-empty-state-default-no-data">No matching data</Trans>}
    subtitle={<Trans id="analytics-empty-state-default-check-filters">Check the filter settings</Trans>}
    width={width}
    height={height}
    image={image}
  />
);

export function isEmptyMetric(
  data: AnalyticsMetric | AnalyticsComputedGraphMetric | {values: any} | undefined,
): boolean {
  if (!data) {
    return true;
  }
  return 'isEmpty' in data && data.isEmpty;
}
